import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateUpdateDismantlingBayRequest,
  IDismantlingBaysResponse,
  ISingleDismantlingBayResponse,
} from "../models/DismantlingBay";

export default function useDismantlingBays(dismantlingBayId?: string) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const dismantlingBays = useQuery({
    queryKey: ["dismantling-bays"],
    queryFn: () => get<IDismantlingBaysResponse>("/dismantlingbay"),
    staleTime: 1000 * 60 * 10,
  });

  const dismantlingBay = useQuery({
    queryKey: ["dismantling-bays", dismantlingBayId],
    queryFn: () => get<ISingleDismantlingBayResponse>(`/dismantlingbay/${dismantlingBayId}`),
    enabled: !!dismantlingBayId,
    staleTime: 1000 * 60 * 10,
  });

  const createDismantlingBay = useMutation(
    (body: ICreateUpdateDismantlingBayRequest) =>
      post<ICreateUpdateDismantlingBayRequest, ISingleDismantlingBayResponse>("/dismantlingbay", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["dismantling-bays"]);
      },
    }
  );

  const updateDismantlingBay = useMutation(
    (body: ICreateUpdateDismantlingBayRequest) =>
      put<ICreateUpdateDismantlingBayRequest, ISingleDismantlingBayResponse>(`/dismantlingbay/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["dismantling-bays"]);
      },
    }
  );

  const deleteDismantlingBay = useMutation(
    (id: string) => del(`/dismantlingbay/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["dismantling-bays"]);
      },
    }
  );

  return {
    dismantlingBays,
    dismantlingBay,
    createDismantlingBay,
    updateDismantlingBay,
    deleteDismantlingBay,
  };
}
